/**
 * VendorApplyForm.jsx — shared public form for /services/apply and /credit/apply.
 * Routes to /api/services/apply or /api/credit/apply based on `mode` prop.
 */
import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { ArrowLeft, Check, Buildings, Bank, Plus, X } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import PhoneField, { isValidPhoneNumber } from '@/components/PhoneField';

const VendorApplyForm = ({ mode }) => {
  const isService = mode === 'service';
  const accent = isService ? '#10b981' : '#f59e0b';
  const Icon = isService ? Buildings : Bank;
  const offerings_label = isService ? 'Service categories you offer' : 'Credit products you offer';
  const offering_placeholder = isService ? 'e.g. LLP registration, GST filing, trademark' : 'e.g. Corporate card, Working capital loan';

  const [form, setForm] = useState({
    vendor_type: mode,
    name: '', tagline: '', description: '',
    contact_email: '', contact_phone: '', website: '',
    service_categories: [], credit_products: [],
    sector_focus: [], stage_focus: [], portfolio_links: [],
    why_us: '',
  });
  const [chipInput, setChipInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const offerings_key = isService ? 'service_categories' : 'credit_products';
  const addChip = () => {
    const v = chipInput.trim();
    if (!v) return;
    setForm((p) => ({ ...p, [offerings_key]: [...p[offerings_key], v] }));
    setChipInput('');
  };

  const submit = async () => {
    if (!form.name.trim() || !form.tagline.trim() || !form.contact_email.trim()) {
      toast.error('Name, tagline and contact email are required');
      return;
    }
    if (form.contact_phone && !isValidPhoneNumber(form.contact_phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/${mode === 'service' ? 'services' : 'credit'}/apply`, form);
      setDone(r.data.application);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="vendor-apply-success">
        <div className="bg-surface border border-border rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-4 inline-flex items-center justify-center" style={{ backgroundColor: `${accent}1a`, color: accent }}><Check size={22} weight="bold" /></div>
          <h2 className="text-2xl font-heading font-bold mb-2" style={{ letterSpacing: '-0.02em' }}>Application received!</h2>
          <p className="text-sm text-text-secondary mb-5">Thanks {done.name}. Sid &amp; Lalith will review your application and reach you at <strong className="text-text-primary">{done.contact_email}</strong> within 48 hours. <br/><br/>Once approved, you&rsquo;ll get an email with a magic link to set your password and access your vendor dashboard.</p>
          <Link to="/vendors" className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: accent }}>Back to Vendor Center</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid={`vendor-apply-${mode}`}>
      <div className="max-w-2xl mx-auto">
        <Link to="/vendors" className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-4"><ArrowLeft size={11} weight="bold" /> Back</Link>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1a`, color: accent }}><Icon size={18} weight="fill" /></span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Vendor application</div>
            <h1 className="text-2xl font-heading font-bold" style={{ letterSpacing: '-0.02em' }}>{isService ? 'Startup Services' : 'Startup Credit'}</h1>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-2xl p-6 space-y-4">
          <F label="Company name *" v={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} testid="vendor-apply-name" />
          <F label="One-liner tagline *" v={form.tagline} onChange={(v) => setForm((p) => ({ ...p, tagline: v }))} testid="vendor-apply-tagline" />
          <T label="Description" v={form.description} onChange={(v) => setForm((p) => ({ ...p, description: v }))} rows={3} testid="vendor-apply-description" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Contact email *" type="email" v={form.contact_email} onChange={(v) => setForm((p) => ({ ...p, contact_email: v }))} testid="vendor-apply-email" />
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Phone</label>
              <PhoneField value={form.contact_phone} onChange={(v) => setForm((p) => ({ ...p, contact_phone: v }))} placeholder="98xxx xxxxx" data-testid="vendor-apply-phone" />
            </div>
          </div>
          <F label="Website" v={form.website} onChange={(v) => setForm((p) => ({ ...p, website: v }))} testid="vendor-apply-website" />

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{offerings_label}</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form[offerings_key].map((v, i) => (
                <span key={i} className="bg-background border border-border rounded-full px-2.5 py-1 text-xs inline-flex items-center gap-1" data-testid={`vendor-apply-chip-${i}`}>
                  {v}<button onClick={() => setForm((p) => ({ ...p, [offerings_key]: p[offerings_key].filter((_, j) => j !== i) }))} className="text-text-muted hover:text-danger"><X size={9} weight="bold" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={chipInput} onChange={(e) => setChipInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChip(); } }} placeholder={offering_placeholder} data-testid="vendor-apply-chip-input" className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              <button onClick={addChip} data-testid="vendor-apply-chip-add" className="text-xs font-semibold px-3 py-2 rounded-lg border border-border hover:border-text-primary inline-flex items-center gap-1"><Plus size={10} weight="bold" /> Add</button>
            </div>
          </div>

          <T label="Why partner with Founderboard?" v={form.why_us} onChange={(v) => setForm((p) => ({ ...p, why_us: v }))} rows={3} testid="vendor-apply-why" />

          <button onClick={submit} disabled={busy} data-testid="vendor-apply-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
            {busy ? 'Submitting\u2026' : 'Submit application'}
          </button>
        </div>
      </div>
    </div>
  );
};

const F = ({ label, v, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);
const T = ({ label, v, onChange, rows = 3, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <textarea value={v} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary leading-relaxed" />
  </div>
);

export default VendorApplyForm;
